import React, { useState, useEffect, useMemo } from 'react';
import { Layers, CircleHelp } from 'lucide-react'; 
import axiosInstance from '@/api/axios';
import { useTranslation } from 'react-i18next';

// --- SHADCN IMPORTS ---
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Import all PNG icons
import bananaIcon from './images/banana.png';
import appleIcon from './images/apple.png';
import plasticBottleIcon from './images/plastic-bottle.png';
import metalCanIcon from './images/metal-can.png';
import plasticBagIcon from './images/plastic-bag.png';
import plasticBagColorIcon from './images/plastic-bag-color.png';
import plasticBagSilverIcon from './images/plastic-bag-silver.png';
import organicMandarinIcon from './images/organic-mandarin.png';
import paperBoxIcon from './images/paper-box.png';
import paperCartonIcon from './images/paper-carton.png';
import paperColorIcon from './images/paper-color.png';
import paperTissueIcon from './images/paper-tissue.png';
import paperOfficeIcon from './images/paper-office.png';
import plasticBagWrapperIcon from './images/plastic-bag-wrapper.png';
import plasticContainerIcon from './images/plastic-container.png';
import drinkCartonIcon from './images/drink-carton.png';
import juicePackageIcon from './images/juice-package.png';
import rubberGlovesIcon from './images/rubber-gloves.png';
import paperCoffeeCupIcon from './images/papercoffeecup.png';
import glassBottleIcon from './images/glass-bottle.png';
import cardboardIcon from './images/cardboard.png';
import organicIcon from './images/organic.png';
import meatIcon from './images/meat.png';
import containerIcon from './images/container.png';
import paperIcon from './images/paper.png';
import metalIcon from './images/metal.png';
import plasticIcon from './images/plastic.png';
import recycleIcon from './images/recycle.png';

const SubclassCardView = ({ 
  id, 
  dayvalue, 
  datevalue = [{ startDate: null, endDate: null, key: 'selection' }],
  isCustomDateRange,
  wasteweight = 0,
  totalwasteproduceddata = 0,
  className = ""
}) => {
  const { t } = useTranslation();
  
  // --- STATE ---
  const [wasteData, setWasteData] = useState([]);
  const [wasteTypeWeights, setWasteTypeWeights] = useState({});
  const [subclassData, setSubclassData] = useState(null);
  const [actualTotalWeight, setActualTotalWeight] = useState(0);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayMode, setDisplayMode] = useState('kg'); 
  const [selectedCategory, setSelectedCategory] = useState('All');

  // --- PNG ICON MAPPING ---
  const getIconPath = (subclassName) => {
    const name = subclassName.toLowerCase().replace(/\s+/g, '-');
    const iconMap = {
      'banana': bananaIcon, 'apple': appleIcon, 'plastic-bottle': plasticBottleIcon,
      'metal-can': metalCanIcon, 'plastic-bag': plasticBagIcon, 'plastic-bag-color': plasticBagColorIcon,
      'plastic-bag-silver': plasticBagSilverIcon, 'organic-mandarin': organicMandarinIcon,
      'paper-box': paperBoxIcon, 'paper-carton': paperCartonIcon, 'paper-color': paperColorIcon,
      'paper-tissue': paperTissueIcon, 'paper-office': paperOfficeIcon,
      'plastic-wrapper': plasticBagWrapperIcon, 'plastic-bag-wrapper': plasticBagWrapperIcon,
      'plastic-container': plasticContainerIcon, 'drink-carton': drinkCartonIcon,
      'juice-package': juicePackageIcon, 'rubber-gloves': rubberGlovesIcon,
      'coffee-cup': paperCoffeeCupIcon, 'paper-coffee-cup': paperCoffeeCupIcon,
      'glass-bottle': glassBottleIcon, 'cardboard': cardboardIcon, 'organic': organicIcon,
      'meat': meatIcon, 'container': containerIcon, 'paper': paperIcon,
      'metal': metalIcon, 'plastic': plasticIcon, 'teabag': organicIcon,
    };
    if (iconMap[name]) return iconMap[name];
    
    if (name.includes('banana')) return bananaIcon;
    if (name.includes('apple')) return appleIcon;
    if (name.includes('bottle') && name.includes('plastic')) return plasticBottleIcon;
    if (name.includes('bottle') && name.includes('glass')) return glassBottleIcon;
    if (name.includes('can') || name.includes('metal')) return metalCanIcon;
    if (name.includes('bag') && name.includes('silver')) return plasticBagSilverIcon;
    if (name.includes('bag') && name.includes('wrapper')) return plasticBagWrapperIcon;
    if (name.includes('bag') && name.includes('color')) return plasticBagColorIcon;
    if (name.includes('bag')) return plasticBagIcon;
    if (name.includes('paper') && name.includes('box')) return paperBoxIcon;
    if (name.includes('paper') && name.includes('carton')) return paperCartonIcon;
    if (name.includes('paper') && name.includes('color')) return paperColorIcon;
    if (name.includes('paper') && name.includes('tissue')) return paperTissueIcon;
    if (name.includes('paper') && name.includes('office')) return paperOfficeIcon;
    if (name.includes('coffee')) return paperCoffeeCupIcon;
    if (name.includes('mandarin') || name.includes('orange')) return organicMandarinIcon;
    if (name.includes('wrapper')) return plasticBagWrapperIcon;
    if (name.includes('plastic') && name.includes('container')) return plasticContainerIcon;
    if (name.includes('carton') && name.includes('drink')) return drinkCartonIcon;
    if (name.includes('juice')) return juicePackageIcon;
    if (name.includes('gloves')) return rubberGlovesIcon;
    if (name.includes('cardboard')) return cardboardIcon;
    if (name.includes('organic')) return organicIcon;
    if (name.includes('meat') || name.includes('food')) return meatIcon;
    if (name.includes('container')) return containerIcon;
    if (name.includes('paper')) return paperIcon;
    if (name.includes('metal')) return metalIcon;
    if (name.includes('plastic')) return plasticIcon;
    return recycleIcon;
  };

  const getColorClass = (index) => {
    const colors = [
      'bg-blue-100', 'bg-emerald-100', 'bg-rose-100', 'bg-cyan-100',
      'bg-purple-100', 'bg-amber-100', 'bg-yellow-100', 'bg-pink-100',
      'bg-indigo-100', 'bg-orange-100', 'bg-teal-100', 'bg-lime-100'
    ];
    return colors[index % colors.length];
  };

  // --- 1. CARD GRID SIZING ---
  const getCardSize = (index) => {
    if (index === 0) return 'col-span-2 row-span-2 md:col-span-4 md:row-span-2'; 
    if (index === 1) return 'col-span-2 row-span-2 md:col-span-2 md:row-span-2';
    if (index === 2 || index === 3) return 'col-span-2 row-span-1 md:col-span-3 md:row-span-1';
    if (index === 4 || index === 5) return 'col-span-1 row-span-1 md:col-span-2 md:row-span-1';
    return 'col-span-1 row-span-1 md:col-span-1 md:row-span-1';
  };

  // --- 2. ADAPTIVE CONTENT CONFIG ---
  const getContentConfig = (index) => {
    if (index === 0) return { 
      padding: 'p-5', iconSize: 'w-10 h-10', titleSize: 'text-2xl', 
      valueSize: 'text-4xl', badgeSize: 'text-sm px-3 py-1', showSubtitle: true, gap: 'gap-4'
    };
    if (index === 1) return { 
      padding: 'p-4', iconSize: 'w-8 h-8', titleSize: 'text-xl', 
      valueSize: 'text-3xl', badgeSize: 'text-xs px-2.5 py-1', showSubtitle: true, gap: 'gap-3'
    };
    if (index <= 3) return { 
      padding: 'p-3', iconSize: 'w-7 h-7', titleSize: 'text-lg', 
      valueSize: 'text-2xl', badgeSize: 'text-xs px-2 py-0.5', showSubtitle: true, gap: 'gap-2'
    };
    if (index <= 5) return { 
      padding: 'p-3', iconSize: 'w-6 h-6', titleSize: 'text-sm', 
      valueSize: 'text-xl', badgeSize: 'text-[10px] px-2 py-0.5', showSubtitle: false, gap: 'gap-1'
    };
    return { 
      padding: 'p-2', iconSize: 'w-5 h-5', titleSize: 'text-xs leading-none', 
      valueSize: 'text-base leading-none', badgeSize: 'text-[9px] px-1.5 py-0', showSubtitle: false, gap: 'gap-1'
    };
  };

  // --- NORMALIZATION ---
  const shouldFilterWasteType = (wasteType) => {
    if (!wasteType) return true;
    const normalized = wasteType.toLowerCase().trim();
    return normalized === 'nothing' || normalized === '';
  };

  const normalizeAndCombineWasteTypes = (name) => {
    if (!name) return '';
    const normalized = name.toLowerCase().replace(/[_-]/g, '').trim();
    if (normalized === 'nothing') return '';
    const combinedMap = {
        'pmd': 'PMD',
        'residual': t('residuallabel') || 'Residual',
        'rest': t('residuallabel') || 'Residual',
        'papercardboard': t('paperlabel') || 'Paper/Cardboard',
        'paper': t('paperlabel') || 'Paper/Cardboard', 
        'organic': t('organiclabel') || 'Organic',
        'glass': 'Glass'
    };
    return combinedMap[normalized] || name.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  };

  const normalizeAndFormatSubclassName = (name) => {
    if (!name) return '';
    const cleanName = name.toLowerCase().replace(/[_\-\s]/g, '');

    const specificTransformations = {
      'organicmandarin': 'Mandarin',
      'organicteabag': 'Teabag',
      'organic': 'Organic (other)',
      'pmd': 'PMD (Other)',
      'plasticbottle': t('plasticBottle') || 'Plastic Bottle',
      'metalcan': t('metalCan') || 'Metal Can',
      'plasticbag': t('plasticBag') || 'Plastic Bag',
      'plasticbagcolor': t('plasticBagColor') || 'Plastic Bag Color',
      'paperbox': t('paperBox') || 'Paper Box',
    };

    if (specificTransformations[cleanName]) {
        return specificTransformations[cleanName];
    }

    if (cleanName.startsWith('organic') && cleanName.length > 7) {
        const stripped = name.replace(/^organic\s*/i, '');
        return stripped.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').trim();
    }

    return name
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
      .trim();
  };

  // --- API CALLS ---
  useEffect(() => {
    const fetchWasteTypeWeights = async () => {
        try {
            let binIdentifiers = Array.isArray(id) ? id : (typeof id === 'string' && id.includes(',') ? id.split(',') : [id]);
            const bins = binIdentifiers.map(b => String(b).trim()).join(',');
            const timeRangeMap = { 'day': 'Day', 'week': 'Week', 'month': 'Month', '3months': '3Month', '6months': '6Month', 'year': 'Year' };
            const normalizedDayValue = dayvalue.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
            const timeRange = timeRangeMap[normalizedDayValue] || 'Month';
            const currentMonth = new Date().getMonth() + 1;
            let season = (currentMonth >= 3 && currentMonth <= 5) ? 'spring' : (currentMonth >= 6 && currentMonth <= 8) ? 'summer' : (currentMonth >= 9 && currentMonth <= 11) ? 'autumn' : 'winter';
            const params = { bins, timeRange, season };

            if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
                const sDate = new Date(datevalue[0].startDate);
                const eDate = new Date(datevalue[0].endDate);
                if (!isNaN(sDate) && !isNaN(eDate)) {
                    params.start_date = sDate.toISOString();
                    params.end_date = eDate.toISOString();
                }
            }

            const response = await axiosInstance.get('/others_page_waste_composition', { params });
            if (response.data) {
                const weights = {};
                if (response.data.paper !== undefined) weights[t('paperlabel') || 'Paper/Cardboard'] = response.data.paper;
                if (response.data.pmd !== undefined) weights['PMD'] = response.data.pmd;
                if (response.data.residual !== undefined) weights[t('residuallabel') || 'Residual'] = response.data.residual;
                if (response.data.organic !== undefined) weights[t('organiclabel') || 'Organic'] = response.data.organic;
                if (response.data.glass !== undefined) weights['Glass'] = response.data.glass;
                setWasteTypeWeights(weights);
                
                // 🔥 FIX: Capture the actual total from API
                setActualTotalWeight(response.data.total || 0);
            }
        } catch (error) {
            console.error('Error fetching weights:', error);
            setWasteTypeWeights({});
            setActualTotalWeight(0);
        }
    };
    fetchWasteTypeWeights();
  }, [id, dayvalue, isCustomDateRange, datevalue, t]);

  useEffect(() => {
    const fetchSubclassData = async () => {
      try {
        setLoading(true);
        setError(null);
        let binIdentifiers = Array.isArray(id) ? id : (typeof id === 'string' && id.includes(',') ? id.split(',') : [id]);
        binIdentifiers = binIdentifiers.map(id => typeof id === 'string' ? id.trim() : String(id)).filter(id => id.length > 0);
        const binResponse = await axiosInstance.get('/get_bins');
        const validBins = binIdentifiers.map(identifier => {
          let bin = binResponse.data.find(b => String(b.unique_identifier).toLowerCase() === String(identifier).toLowerCase());
          if (!bin && !isNaN(identifier)) bin = binResponse.data.find(b => b.id === parseInt(identifier));
          return bin;
        }).filter(b => b !== undefined);

        if (validBins.length === 0) throw new Error('No bins found');
        const binIds = validBins.map(bin => bin.id).join(',');
        let subclassUrl = `/subclass_waste_composition/${binIds}/${dayvalue.toLowerCase()}/`;
        
        const subclassParams = new URLSearchParams();
        if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
            const sDate = datevalue[0].startDate instanceof Date ? datevalue[0].startDate : new Date(datevalue[0].startDate);
            const eDate = datevalue[0].endDate instanceof Date ? datevalue[0].endDate : new Date(datevalue[0].endDate);
            if (!isNaN(sDate) && !isNaN(eDate)) {
                subclassParams.append('start_date', sDate.toISOString());
                subclassParams.append('end_date', eDate.toISOString());
            }
        }
        if (subclassParams.toString()) subclassUrl += `?${subclassParams.toString()}`;

        const subclassResponse = await axiosInstance.get(subclassUrl);
        
        if (!subclassResponse.data) {
            setSubclassData({});
            return;
        }

        const combinedData = {};
        Object.keys(subclassResponse.data).forEach(wasteType => {
            if (shouldFilterWasteType(wasteType)) return;
            const normalizedType = normalizeAndCombineWasteTypes(wasteType);
            if (!normalizedType) return;
            if (!combinedData[normalizedType]) combinedData[normalizedType] = {};

            Object.keys(subclassResponse.data[wasteType]).forEach(subClass => {
                if (shouldFilterWasteType(subClass)) return;
                const normalizedSubClass = normalizeAndFormatSubclassName(subClass);
                const value = subclassResponse.data[wasteType][subClass];
                if (normalizedSubClass && value > 0) {
                    combinedData[normalizedType][normalizedSubClass] = (combinedData[normalizedType][normalizedSubClass] || 0) + value;
                }
            });
        });
        setSubclassData(combinedData);
      } catch (err) {
        if (err.response && err.response.status === 404) {
            setSubclassData({});
        } else {
            console.error('Error subclass data:', err);
            setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSubclassData();
  }, [dayvalue, id, isCustomDateRange, datevalue, t]);

  useEffect(() => {
    if (!subclassData) {
        setWasteData([]);
        return;
    }
    const calculatedItems = [];
    Object.keys(subclassData).forEach(categoryName => {
        const categoryItems = subclassData[categoryName];
        const categoryRawTotal = Object.values(categoryItems).reduce((sum, val) => sum + val, 0);
        const categoryRealWeight = wasteTypeWeights[categoryName] || 0;

        if (categoryRawTotal > 0 && categoryRealWeight > 0) {
            Object.keys(categoryItems).forEach(itemName => {
                const rawValue = categoryItems[itemName];
                const distributionPercentage = rawValue / categoryRawTotal;
                const itemRealWeight = distributionPercentage * categoryRealWeight;
                const percentageOfTotal = wasteweight > 0 ? (itemRealWeight / wasteweight) * 100 : 0;
                const percentageOfCategory = categoryRealWeight > 0 ? (itemRealWeight / categoryRealWeight) * 100 : 0;
                
                calculatedItems.push({
                    name: itemName,
                    weight: itemRealWeight,
                    percentageOfTotal: percentageOfTotal,
                    percentageOfCategory: percentageOfCategory,
                    wasteType: categoryName
                });
            });
        }
    });
    setWasteData(calculatedItems.sort((a, b) => b.weight - a.weight));
  }, [subclassData, wasteTypeWeights, wasteweight]);

  // --- FILTERING ---
  const availableCategories = useMemo(() => {
    const categories = new Set(wasteData.map(item => item.wasteType));
    const sorted = Array.from(categories).sort();
    return ['All', ...sorted];
  }, [wasteData]);

  const filteredWasteData = useMemo(() => {
    if (selectedCategory === 'All') return wasteData;
    return wasteData.filter(item => item.wasteType === selectedCategory);
  }, [wasteData, selectedCategory]);

  // 🔥 FIX: Use actual API totals instead of calculating from filtered items
  const filteredTotalWeight = useMemo(() => {
    if (selectedCategory === 'All') {
      return actualTotalWeight; // Use API total for "All"
    }
    // For specific categories, use the real weight from wasteTypeWeights
    return wasteTypeWeights[selectedCategory] || 0;
  }, [selectedCategory, actualTotalWeight, wasteTypeWeights]);


  // --- RENDER ---
  return (
    <div className={`flex flex-col h-full w-full overflow-hidden ${className}`}>
      
      {/* HEADER */}
      <div className="flex-shrink-0 mb-2 px-4 pt-4 z-10">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                {t('subwastecardlabel') || 'Sub-waste Heatmap'}
              </h4>
              <p className="text-xs text-gray-500 font-medium mt-0.5">
                {isCustomDateRange ? t('customRange') : (t(dayvalue.toLowerCase()) || dayvalue)}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* 1. TOOLTIP (LEFT) */}
              <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="cursor-help flex items-center justify-center p-1">
                            <CircleHelp className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors" />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                        <p>A visual breakdown of the sub-waste categories across your selected bins and timeframe. Each button represents the waste that has been discarded in that specific bin.</p>
                    </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* 2. TOGGLE BUTTONS (RIGHT) */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 border border-gray-200">
                <button onClick={() => setDisplayMode('kg')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${displayMode === 'kg' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>KG</button>
                <button onClick={() => setDisplayMode('percentage')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${displayMode === 'percentage' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>%</button>
              </div>
            </div>
          </div>

          <div className="relative">
             <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 mask-linear-fade">
                {availableCategories.map(cat => (
                    <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                            selectedCategory === cat 
                            ? 'bg-primary text-white border-primary shadow-md' 
                            : 'bg-white text-gray-600 border-gray-200 hover:text-primary hover:bg-gray-50'
                        }`}
                    >
                        {cat === 'All' ? <span className="flex items-center gap-1"><Layers className="w-3 h-3"/> {t('All') || 'All'}</span> : cat}
                    </button>
                ))}
             </div>
          </div>
        </div>
      </div>
      
      {/* CARD GRID */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-red-500">
            {t('errorLoadingData') || 'Error loading data'}
          </div>
        ) : filteredWasteData.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-6 auto-rows-[120px] gap-3 grid-flow-dense pb-4">
            {filteredWasteData.map((item, index) => {
              
              const cardSizeClass = getCardSize(index);
              const config = getContentConfig(index);
              const iconPath = getIconPath(item.name);
              
              // --- DYNAMIC CALCULATION ---
              // Calculate percentage relative to the current visible total (filteredTotalWeight)
              const relativePercentage = filteredTotalWeight > 0 
                ? (item.weight / filteredTotalWeight) * 100 
                : 0;
              
              return (
                <div
                  key={`${item.wasteType}-${item.name}`}
                  className={`${item.colorClass || getColorClass(index)} ${cardSizeClass} ${config.padding} rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between overflow-hidden`}
                >
                  <div className={`flex items-start justify-between min-w-0 ${config.gap}`}>
                    <div className="flex-shrink-0 bg-white/60 backdrop-blur-sm rounded-xl p-1.5 shadow-sm">
                      <img 
                        src={iconPath} 
                        alt={item.name}
                        className={`${config.iconSize} object-contain transition-all`}
                        onError={(e) => { e.target.src = recycleIcon; }}
                      />
                    </div>
                    
                    {/* Badge: Shows the opposite unit of the main display */}
                    <span className={`flex-shrink-0 font-bold bg-white/70 rounded-full text-gray-700 whitespace-nowrap ${config.badgeSize}`}>
                      {displayMode === 'kg' 
                        ? `${relativePercentage.toFixed(1)}%`
                        : `${item.weight.toFixed(2)} kg`
                      }
                    </span>
                  </div>
                  
                  <div className="min-w-0">
                    <h3 
                      className={`font-semibold text-gray-800 leading-tight truncate ${config.titleSize}`}
                      title={item.name}
                    >
                      {item.name}
                    </h3>
                    
                    {selectedCategory === 'All' && config.showSubtitle && (
                        <div className="text-[9px] text-gray-500 font-medium uppercase tracking-wider mb-1 opacity-80 truncate">
                            {item.wasteType}
                        </div>
                    )}

                    <div className="flex items-baseline gap-1 mt-0.5 min-w-0">
                      {displayMode === 'kg' ? (
                        <>
                          <span className={`font-bold text-gray-900 leading-none truncate ${config.valueSize}`}>
                            {item.weight.toFixed(2)}
                          </span>
                          <span className="text-[10px] text-gray-600 font-medium flex-shrink-0">kg</span>
                        </>
                      ) : (
                        <>
                          <span className={`font-bold text-gray-900 leading-none truncate ${config.valueSize}`}>
                            {relativePercentage.toFixed(1)}
                          </span>
                          <span className="text-[10px] text-gray-600 font-medium flex-shrink-0">%</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
            <Layers className="w-8 h-8 opacity-20"/>
            <p>{t('noDataForCategory') || 'No items found in this category'}</p>
          </div>
        )}
      </div>

    {/* FOOTER */}
      <div className="flex-shrink-0 pt-3 mt-auto border-t border-gray-100 px-4 pb-4 bg-white/50 backdrop-blur-sm z-10">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-500 font-bold">
              {selectedCategory === 'All' ? (t('total') || 'Total') : selectedCategory}:
            </span>
            <span className="text-lg font-extrabold text-primary">
              {displayMode === 'kg' ? (
                <>
                  {filteredTotalWeight.toFixed(2)} <span className="text-sm font-medium text-gray-500">kg</span>
                </>
              ) : (
                <>
                  100.0 <span className="text-sm font-medium text-gray-500">%</span>
                </>
              )}
            </span>
          </div>
          <div className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
            {filteredWasteData.length} {t('items') || 'items'}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SubclassCardView;